import { Sparkles, TrendingUp, AlertCircle, Lightbulb, BarChart3, Target, Zap, Award } from "lucide-react";
import { LandingHeader, LandingFooter } from "@/components/landing";
import {
  FeatureHero,
  FeatureCTA,
  RelatedFeatures,
  Breadcrumb,
  FeatureSection,
  BenefitCard,
  ExampleBlock,
} from "@/components/product";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const analysisFeatures = [
  {
    icon: BarChart3,
    title: "Rentabilité vs benchmarks",
    description: "Comparez vos marges aux moyennes du secteur BTP.",
    gradient: "from-[#10B981] to-[#059669]",
  },
  {
    icon: TrendingUp,
    title: "Évolution des coûts",
    description: "Identifiez les tendances et les dérives dans le temps.",
    gradient: "from-[#3B82F6] to-[#2563EB]",
  },
  {
    icon: AlertCircle,
    title: "Risques identifiés",
    description: "Détection précoce des retards et dépassements potentiels.",
    gradient: "from-[#EF4444] to-[#DC2626]",
  },
  {
    icon: Target,
    title: "Comparaison inter-chantiers",
    description: "Apprenez de vos meilleurs chantiers pour les prochains.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
];

const recommendations = [
  {
    icon: Lightbulb,
    title: "Actions prioritaires",
    description: "Recommandations concrètes pour améliorer votre marge.",
    gradient: "from-[#F59E0B] to-[#D97706]",
  },
  {
    icon: AlertCircle,
    title: "Alertes sur les dérives",
    description: "Notification immédiate quand quelque chose dérape.",
    gradient: "from-[#EF4444] to-[#DC2626]",
  },
  {
    icon: Zap,
    title: "Optimisation équipe",
    description: "Suggestions pour mieux utiliser vos ressources humaines.",
    gradient: "from-[#8B5CF6] to-[#7C3AED]",
  },
  {
    icon: Award,
    title: "Score de santé",
    description: "Un score de 0 à 100 pour évaluer la santé de chaque chantier.",
    gradient: "from-[#10B981] to-[#059669]",
  },
];

const plans = [
  { name: "Starter", analyses: "60", period: "/mois" },
  { name: "Pro", analyses: "240", period: "/mois" },
  { name: "Entreprise", analyses: "Illimité", period: "" },
];

const AnalyseIAFeature = () => {
  usePageMeta({ title: "Analyse IA chantier BTP — B8ild", description: "L'IA B8ild analyse vos chantiers et détecte les anomalies avant qu'elles ne deviennent des pertes. Recommandations automatiques.", url: "https://b8ild.fr/product/analyse-ia" });
  return (
    <div className="min-h-screen bg-[#0F172A]">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Analyse IA chantier BTP — B8ild",
        description: "L'IA B8ild analyse vos chantiers et détecte les anomalies avant qu'elles ne deviennent des pertes. Recommandations automatiques.",
        url: "https://b8ild.fr/product/analyse-ia",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Produit", item: "https://b8ild.fr/product" },
          { "@type": "ListItem", position: 3, name: "Analyse IA", item: "https://b8ild.fr/product/analyse-ia" },
        ],
      }} />
      <LandingHeader />

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 pt-24">
        <Breadcrumb
          items={[
            { label: "Produit", href: "/product" },
            { label: "Analyse IA" },
          ]}
        />
      </div>

      {/* Hero */}
      <FeatureHero
        badge="Analyse IA"
        title="L'intelligence artificielle"
        titleHighlight="au service de votre rentabilité."
        subtitle="B8ild analyse vos données et vous donne des recommandations concrètes pour améliorer vos marges."
        icon={Sparkles}
        gradient="from-[#EC4899] to-[#DB2777]"
      />

      {/* What IA analyzes */}
      <FeatureSection
        title="Ce que l'IA analyse"
        subtitle="Une analyse complète de vos données"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {analysisFeatures.map((feature, index) => (
            <BenefitCard key={index} {...feature} />
          ))}
        </div>
      </FeatureSection>

      {/* What IA recommends */}
      <FeatureSection
        variant="dark"
        title="Ce que l'IA vous recommande"
        subtitle="Des actions concrètes, pas du blabla"
      >
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
          {recommendations.map((rec, index) => (
            <BenefitCard key={index} {...rec} />
          ))}
        </div>
      </FeatureSection>

      {/* Example recommendation */}
      <FeatureSection title="Exemple de recommandation IA">
        <div className="max-w-3xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-[#EC4899]/30 to-transparent">
            <div className="rounded-2xl bg-[#EC4899]/5 backdrop-blur-xl p-6">
              <div className="flex items-start gap-4">
                <div className="h-10 w-10 rounded-[10px] bg-[#131318] border border-white/[0.06] flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-[18px] w-[18px] text-white/50" strokeWidth={2.5} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#EC4899] font-medium mb-2">
                    Recommandation IA
                  </p>
                  <p className="text-lg text-white leading-relaxed">
                    "Votre coût matériaux est <span className="font-semibold text-[#F59E0B]">23% supérieur</span> à la moyenne de vos chantiers similaires. Vérifiez les factures fournisseur ou négociez de meilleurs tarifs."
                  </p>
                  <div className="flex items-center gap-4 mt-4">
                    <span className="text-sm text-white/50">Impact estimé :</span>
                    <span className="text-sm font-semibold text-[#10B981]">+2 800 € de marge</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Analysis Preview */}
      <FeatureSection variant="dark" title="Tableau de bord IA">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl p-[1px] bg-gradient-to-b from-white/[0.15] to-white/[0.02]">
            <div className="rounded-2xl bg-[#0F0F14]/90 backdrop-blur-xl overflow-hidden">
              {/* Header */}
              <div className="px-6 py-4 border-b border-white/[0.05] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-[8px] bg-[#131318] border border-white/[0.06] flex items-center justify-center">
                    <Sparkles className="h-4 w-4 text-white/50" strokeWidth={2.5} />
                  </div>
                  <h3 className="text-lg font-semibold text-white">Analyse IA — Villa Durand</h3>
                </div>
                <span className="text-sm text-white/40">Générée il y a 2h</span>
              </div>

              <div className="p-6">
                {/* Score */}
                <div className="flex items-center justify-center gap-8 mb-8">
                  <div className="text-center">
                    <div className="relative h-32 w-32">
                      <svg className="h-32 w-32 transform -rotate-90">
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="none"
                          className="text-white/[0.05]"
                        />
                        <circle
                          cx="64"
                          cy="64"
                          r="56"
                          stroke="url(#gradient)"
                          strokeWidth="8"
                          fill="none"
                          strokeLinecap="round"
                          strokeDasharray={`${(82 / 100) * 352} 352`}
                        />
                        <defs>
                          <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="#10B981" />
                            <stop offset="100%" stopColor="#F59E0B" />
                          </linearGradient>
                        </defs>
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">82</span>
                      </div>
                    </div>
                    <p className="text-sm text-white/50 mt-2">Score de santé</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-[#10B981]" />
                      <span className="text-white/70">Marge au-dessus de la moyenne</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-[#F59E0B]" />
                      <span className="text-white/70">Coûts matériaux à surveiller</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-3 w-3 rounded-full bg-[#10B981]" />
                      <span className="text-white/70">Planning dans les temps</span>
                    </div>
                  </div>
                </div>

                {/* Key metrics */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3 text-center">
                    <p className="text-xs text-white/40 mb-1">ROI</p>
                    <p className="text-xl font-bold text-[#10B981]">+24%</p>
                  </div>
                  <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3 text-center">
                    <p className="text-xs text-white/40 mb-1">CA/jour</p>
                    <p className="text-xl font-bold text-white">2 450€</p>
                  </div>
                  <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3 text-center">
                    <p className="text-xs text-white/40 mb-1">Indice perf.</p>
                    <p className="text-xl font-bold text-white">1.12</p>
                  </div>
                  <div className="rounded-xl bg-white/[0.02] border border-white/[0.05] p-3 text-center">
                    <p className="text-xs text-white/40 mb-1">Rent./h</p>
                    <p className="text-xl font-bold text-[#F59E0B]">38€</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </FeatureSection>

      {/* Plans */}
      <FeatureSection title="Inclus selon votre plan">
        <div className="max-w-2xl mx-auto">
          <div className="grid grid-cols-3 gap-4">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`rounded-xl p-4 text-center ${
                  plan.name === "Pro"
                    ? "bg-[#F59E0B]/10 border border-[#F59E0B]/30"
                    : "bg-white/[0.02] border border-white/[0.05]"
                }`}
              >
                <p className={`text-sm font-medium ${plan.name === "Pro" ? "text-[#F59E0B]" : "text-white/50"}`}>
                  {plan.name}
                </p>
                <p className="text-2xl font-bold text-white mt-1">
                  {plan.analyses}
                  <span className="text-sm font-normal text-white/40">{plan.period}</span>
                </p>
                <p className="text-xs text-white/40 mt-1">analyses</p>
              </div>
            ))}
          </div>
        </div>
      </FeatureSection>

      {/* Pourquoi c'est important */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Le problème que vous ne voyez pas sur 8 chantiers
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Vous avez 8 chantiers ouverts. Tous semblent "dans les clous". Les achats sont faits, les equipes sont en place. Mais un chantier de renovation a 35 000 EUR a un probleme invisible : le rythme de consommation du budget est 15% plus rapide que l'avancement physique.
              </p>
              <p>
                A ce rythme, le chantier sera deficitaire dans 12 jours. Un oeil humain ne voit pas ce pattern sur 8 chantiers simultanes.
              </p>
              <p>
                L'analyse IA de B8ild detecte ces divergences. Score sante de 0 a 100. En dessous de 60 : alerte. Le systeme repere les depassements de budget materiaux, les derives de main d'oeuvre, et les retards qui s'accumulent. Pas de jargon. Un score, une explication en 2 phrases, une recommandation concrete.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions frequentes */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions frequentes
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment l'IA peut aider a gerer un chantier ?",
                  a: "L'IA compare vos donnees reelles (couts, avancement, planning) avec des patterns connus. Si vos achats materiaux depassent 60% du budget alors que vous n'etes qu'a 40% d'avancement, c'est un signal. L'IA le detecte avant vous. Elle donne un score de sante (0-100) et des recommandations : renegocier un prix, reaffecter une equipe, alerter sur un risque de retard.",
                },
                {
                  q: "Les predictions de l'IA sont-elles fiables ?",
                  a: "L'IA se base sur VOS donnees reelles, pas sur des moyennes nationales. Plus vous utilisez B8ild, plus les predictions sont precises. Apres 3-4 chantiers, le systeme connait votre rythme de travail, vos fournisseurs habituels, et vos marges types. La marge d'erreur descend sous 5% apres 6 mois d'utilisation.",
                },
                {
                  q: "Quel ROI attendre de l'analyse IA ?",
                  a: "Les utilisateurs B8ild qui suivent les alertes IA economisent en moyenne 2 000 a 5 000 EUR/an en evitant des derives non detectees. Sur un abonnement Pro a 99 EUR/mois (1 188 EUR/an), le retour sur investissement est positif des le 2e chantier sauve.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <FeatureCTA
        title="Optimisez avec l'IA"
        subtitle="Des recommandations personnalisées pour chaque chantier."
      />

      {/* Related Features */}
      <RelatedFeatures currentFeatureId="analyse-ia" />

      <LandingFooter />
    </div>
  );
};

export default AnalyseIAFeature;
